// S3 right pane: the line inspector. Edits the selected draft root line — qty,
// UoM, scrap %, consume-at-step, optional flag, notes, and the substitute list
// (§2.7, §2.8). Controlled: every edit calls `onChange` with the next line; the
// screen owns the line array and the save. Read-only versions disable everything.

import { useTranslation } from "react-i18next";
import type { components } from "@assessly/api-client";
import { UomSelect } from "./pickers";
import { ProductPicker } from "../../components/product-picker";
import { freshKey, type EditableLine, type EditableSubstitute } from "./helpers";

type Product = components["schemas"]["Product"];
type Uom = components["schemas"]["Uom"];

export function LineInspector({
  line,
  products,
  uoms,
  readOnly,
  onChange,
  onRemove,
}: {
  line: EditableLine | undefined;
  products: readonly Product[];
  uoms: readonly Uom[];
  readOnly: boolean;
  onChange: (next: EditableLine) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();

  // Resolve an already-chosen product's label from the loaded page for the
  // typeahead's selected view (a >page-limit product falls back to a short id).
  const labelFor = (productId: string): string | undefined => {
    const p = products.find((x) => x.id === productId);
    return p !== undefined ? `${p.sku} — ${p.name}` : undefined;
  };

  if (line === undefined) {
    return (
      <p className="bom-inspector-empty" data-testid="inspector-empty">
        {t("catalogue.bomEditor.inspector.selectPrompt")}
      </p>
    );
  }

  const patch = (fields: Partial<EditableLine>) => {
    onChange({ ...line, ...fields });
  };

  const setSubstitute = (index: number, fields: Partial<EditableSubstitute>) => {
    const substitutes = line.substitutes.map((s, i) => (i === index ? { ...s, ...fields } : s));
    onChange({ ...line, substitutes });
  };

  const addSubstitute = () => {
    const next: EditableSubstitute = {
      key: freshKey("sub"),
      substitute_product_id: "",
      priority: line.substitutes.length + 1,
      qty_factor: 1,
    };
    onChange({ ...line, substitutes: [...line.substitutes, next] });
  };

  const removeSubstitute = (index: number) => {
    onChange({ ...line, substitutes: line.substitutes.filter((_, i) => i !== index) });
  };

  const excludeSubstitutes = new Set<string>([
    line.component_product_id,
    ...line.substitutes.map((s) => s.substitute_product_id).filter((x) => x.length > 0),
  ]);

  return (
    <div className="bom-inspector" data-testid="line-inspector">
      <div className="bom-field">
        <label htmlFor="insp-component">{t("catalogue.bomEditor.inspector.component")}</label>
        <ProductPicker
          id="insp-component"
          value={line.component_product_id}
          disabled={readOnly}
          resolveLabel={labelFor}
          placeholder={t("catalogue.bomEditor.inspector.componentPlaceholder")}
          onChange={(component_product_id) => {
            patch({ component_product_id });
          }}
        />
      </div>

      <div className="bom-field-row">
        <div className="bom-field">
          <label htmlFor="insp-qty">{t("catalogue.bomEditor.inspector.qty")}</label>
          <input
            id="insp-qty"
            type="number"
            min={0}
            step="any"
            disabled={readOnly}
            value={Number.isNaN(line.qty) ? "" : line.qty}
            onChange={(e) => {
              patch({ qty: e.target.value === "" ? Number.NaN : Number(e.target.value) });
            }}
          />
        </div>
        <div className="bom-field">
          <label htmlFor="insp-uom">{t("catalogue.bomEditor.inspector.uom")}</label>
          <UomSelect
            id="insp-uom"
            uoms={uoms}
            value={line.uom_id}
            disabled={readOnly}
            onChange={(uom_id) => {
              patch({ uom_id });
            }}
          />
        </div>
        <div className="bom-field">
          <label htmlFor="insp-scrap">{t("catalogue.bomEditor.inspector.scrapPct")}</label>
          <input
            id="insp-scrap"
            type="number"
            min={0}
            max={100}
            step="any"
            disabled={readOnly}
            value={Number.isNaN(line.scrap_pct) ? "" : line.scrap_pct}
            onChange={(e) => {
              patch({ scrap_pct: e.target.value === "" ? Number.NaN : Number(e.target.value) });
            }}
          />
        </div>
      </div>

      <div className="bom-field">
        <label htmlFor="insp-step">{t("catalogue.bomEditor.inspector.consumeAtStep")}</label>
        <input
          id="insp-step"
          type="number"
          min={0}
          step={1}
          disabled={readOnly}
          placeholder={t("catalogue.bomEditor.inspector.atWoStart")}
          value={line.consume_at_step_no ?? ""}
          onChange={(e) => {
            patch({ consume_at_step_no: e.target.value === "" ? null : Number(e.target.value) });
          }}
        />
      </div>

      <label className="bom-checkbox">
        <input
          type="checkbox"
          disabled={readOnly}
          checked={line.is_optional}
          onChange={(e) => {
            patch({ is_optional: e.target.checked });
          }}
        />
        {t("catalogue.bomEditor.inspector.optional")}
      </label>

      <div className="bom-field">
        <label htmlFor="insp-notes">{t("catalogue.bomEditor.inspector.notes")}</label>
        <input
          id="insp-notes"
          type="text"
          disabled={readOnly}
          value={line.notes ?? ""}
          onChange={(e) => {
            patch({ notes: e.target.value === "" ? null : e.target.value });
          }}
        />
      </div>

      <fieldset className="bom-substitutes">
        <legend>{t("catalogue.bomEditor.inspector.substitutes")}</legend>
        {line.substitutes.map((sub, index) => (
          <div className="bom-substitute-row" key={sub.key} data-testid="substitute-row">
            <ProductPicker
              value={sub.substitute_product_id}
              disabled={readOnly}
              resolveLabel={labelFor}
              exclude={
                new Set([...excludeSubstitutes].filter((x) => x !== sub.substitute_product_id))
              }
              placeholder={t("catalogue.bomEditor.inspector.substitutePlaceholder")}
              onChange={(substitute_product_id) => {
                setSubstitute(index, { substitute_product_id });
              }}
            />
            <label className="bom-inline-field">
              {t("catalogue.bomEditor.inspector.priority")}
              <input
                type="number"
                min={1}
                step={1}
                disabled={readOnly}
                value={sub.priority}
                onChange={(e) => {
                  setSubstitute(index, { priority: Number(e.target.value) });
                }}
              />
            </label>
            <label className="bom-inline-field">
              {t("catalogue.bomEditor.inspector.qtyFactor")}
              <input
                type="number"
                min={0}
                step="any"
                disabled={readOnly}
                value={sub.qty_factor}
                onChange={(e) => {
                  setSubstitute(index, { qty_factor: Number(e.target.value) });
                }}
              />
            </label>
            {readOnly ? null : (
              <button
                type="button"
                className="ds-lnk ds-lnk-danger"
                onClick={() => {
                  removeSubstitute(index);
                }}
              >
                {t("catalogue.bomEditor.inspector.removeSubstitute")}
              </button>
            )}
          </div>
        ))}
        {readOnly ? null : (
          <button type="button" className="ds-lnk" onClick={addSubstitute}>
            {t("catalogue.bomEditor.inspector.addSubstitute")}
          </button>
        )}
      </fieldset>

      {readOnly ? null : (
        <button type="button" className="ds-lnk ds-lnk-danger bom-remove-line" onClick={onRemove}>
          {t("catalogue.bomEditor.inspector.removeLine")}
        </button>
      )}
    </div>
  );
}
